"use client";

import { IconSquareBehindSquare1 } from "central-icons-outlined/IconSquareBehindSquare1";
import { AnimatePresence, m } from "motion/react";
import { useId, useState } from "react";
import Comparison from "@/components/preview/comparison";
import DemoHint from "@/components/preview/demo-hint";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { Button } from "@/components/ui/button";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { useTimedState } from "@/hooks/use-timed-state";
import CustomCheckmarkIcon from "@/icons/custom-checkmark-icon";
import DemoInput from "../demo-input";

const CODE = "123456";
const BLOCKED_NOTE_MS = 1600;

const CODE_INPUT_CLASSNAME =
  "font-berkeley-mono tracking-[0.3em] placeholder:font-sans placeholder:tracking-normal";

const noteTransition = { duration: 0.15, ease: "easeOut" as const };

function digitsOnly(value: string) {
  return value.replace(/\D/g, "").slice(0, CODE.length);
}

function StatusLine({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="absolute inset-x-0 top-full flex h-10 items-center justify-center"
      role="status"
    >
      <AnimatePresence>
        {children && (
          <m.span
            animate={{ opacity: 1 }}
            className="select-none text-gray-1000 text-sm"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            transition={noteTransition}
          >
            {children}
          </m.span>
        )}
      </AnimatePresence>
    </div>
  );
}

function CodeField({
  id,
  label,
  onChange,
  onPaste,
  value,
}: {
  id: string;
  label: string;
  onChange: (value: string) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  value: string;
}) {
  return (
    <div className="flex w-full max-w-56 flex-col gap-2">
      <label className="select-none text-gray-1000 text-sm" htmlFor={id}>
        {label}
      </label>
      <DemoInput
        autoComplete="one-time-code"
        id={id}
        inputClassName={CODE_INPUT_CLASSNAME}
        inputMode="numeric"
        maxLength={CODE.length}
        onChange={(event) => onChange(digitsOnly(event.target.value))}
        onPaste={onPaste}
        placeholder="Enter code"
        size="sm"
        type="text"
        value={value}
      />
    </div>
  );
}

export default function AccessibilityPasteDemo() {
  const badId = useId();
  const goodId = useId();
  const [badValue, setBadValue] = useState("");
  const [goodValue, setGoodValue] = useState("");
  const [blocked, setBlocked] = useTimedState(false, BLOCKED_NOTE_MS);
  const { copy, isCopied } = useCopyToClipboard();

  return (
    <Comparison
      bad={
        <div className="relative flex w-full justify-center">
          <CodeField
            id={badId}
            label="One-time password"
            onChange={setBadValue}
            onPaste={(event) => {
              event.preventDefault();
              setBlocked(true);
            }}
            value={badValue}
          />
          <StatusLine>{blocked && "pasting is disabled"}</StatusLine>
        </div>
      }
      footer={
        <Button
          leftIcon={
            <AnimatedIcon
              active={isCopied}
              activeIcon={
                <CustomCheckmarkIcon className="size-4.5 text-gray-1000 will-change-transform" />
              }
              idleIcon={
                <IconSquareBehindSquare1 className="size-4.5 text-gray-1000 will-change-transform" />
              }
            />
          }
          onClick={() => copy(CODE)}
          rounded
          size="sm"
          type="button"
          variant="secondary"
        >
          Copy code
        </Button>
      }
      good={
        <CodeField
          id={goodId}
          label="One-time password"
          onChange={setGoodValue}
          value={goodValue}
        />
      }
      panelClassName="h-44 gap-10 px-6"
      stackBelow="md"
      topBar={<DemoHint>Copy the code and paste it into both fields</DemoHint>}
    />
  );
}
